// Plan 260815-lvl (M005 Hero-Speicherfenster und Preload-Entlastung): the decoded-bitmap
// window fraction. FRAME_WINDOW_FRACTION * totalFrames, rounded and floored at 4, is the
// number of frames kept decoded on EACH side of the currently visible frame - see the
// comment on this.frameWindowRadius in init() for why 0.12 lands at the same physical
// scroll distance on both formats despite the different frame counts.
const FRAME_WINDOW_FRACTION = 0.12;

// Fix H5 Frame-Verfuegbarkeit (debug session hero-scrub-ruckelt-live, 2026-08-16): M005's
// window radius above is now used ONLY to decide what to actively fetch/prioritise, never
// what to evict. Eviction happens over this much wider, generous radius instead - a real
// hysteresis margin, so a moving center alone can no longer throw away frames the loader
// only just fetched or is about to need again a moment later. Doubling still bounds memory
// far below M004's unconditional full-sequence residency (measured 471 MB, H3) while giving
// the loader room to actually keep up with a fling.
const EVICTION_HYSTERESIS_FACTOR = 2;

// Fix H5, lever 3: how many frames the background loader fetches in parallel once it is
// allowed to run. A single sequential fetch-at-a-time loop (the M005 design) serialises
// every fetch behind the one before it; a small concurrency window lets the loader actually
// close the gap to a moving target instead of falling further behind with every frame.
const MAX_CONCURRENT_LOADS = 3;

if (!customElements.get('hero-scroll-story')) {
  customElements.define(
    'hero-scroll-story',
    class HeroScrollStory extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          track: this.querySelector('.hero-scroll__track'),
          sticky: this.querySelector('.hero-scroll__sticky'),
          still: this.querySelector('.hero-scroll__still'),
          canvas: this.querySelector('.hero-scroll__canvas'),
          lines: Array.from(this.querySelectorAll('.hero-scroll__line')),
          hint: this.querySelector('.hero-scroll__hint'),
        };
        this.ctx = this.elements.canvas ? this.elements.canvas.getContext('2d') : null;
        this.frames = new Map();
        this.template = '';
        this.totalFrames = 0;
        this.stageCount = 0;
        this.currentStage = -1;
        this.canvasReady = false;
        this.hintGone = false;
        this.sectionHeightPx = 0;
        this.viewportHeightPx = 0;
        this.ticking = false;
        this.boundOnScroll = this.onScroll.bind(this);
        this.boundOnResize = this.measure.bind(this);
        // Fires once the scroll gesture has settled: a 150ms debounce riding the
        // already-registered scroll listener (no second listener added) - drives both the
        // active-line switch and the JS-based chapter snap. Deliberately NOT gated on the
        // native 'scrollend' event: measured live (tools/lab/hero-scrub-trace.mjs,
        // hero-scrub-ruckelt-live) that Chrome fires 'scrollend' up to 100+ times during a
        // single continuous discrete-wheel-notch gesture (no momentum phase to coalesce
        // against) instead of once at the true end - acting on it would have reintroduced
        // the exact bug this fix removes, just in JS instead of CSS. A fixed quiet-period
        // debounce has no such dependency on how the browser buckets input into "gestures".
        this.boundOnSettle = this.onSettle.bind(this);
        this.snapArmed = false;
        this.lastProgress = 0;
        this.settleDebounceTimer = null;
        this.snapObserver = null;
        // Plan 260815-lvl instance fields - simple state, no new public interface. The
        // measurement tools (tools/lab/hero-scrub-trace.mjs, snap-exit-check.mjs) read these
        // directly, the same way they already read el.totalFrames and el.snapArmed.
        this.closedBitmapCount = 0;
        this.loaderQueue = [];
        this.loaderRunning = false;
        this.redrawRequested = false;
        this.failedFrames = new Set();
        this.frameWindowRadius = 0;
        this.bytesPerFrame = 0;
        this.lastTargetIndex = 0;
        this.lastDrawnIndex = 0;
        // Fix H5 Frame-Verfuegbarkeit (hero-scrub-ruckelt-live, 2026-08-16):
        this.evictionRadius = 0; // lever 1 - wider-than-window eviction cap, set in init()
        this.inFlight = new Set(); // frames currently being fetched, any path
        this.lastCenter = 0; // previous ensureWindow() center, for direction bias (lever 5)
        this.gestureActive = false; // true between the first onScroll() and onSettle() (lever 3)
      }

      connectedCallback() {
        if (!this.elements.track || !this.elements.sticky || !this.elements.still || !this.elements.canvas) {
          return;
        }

        // Theme editor: never pin, never scrub. The editor injects its own scroll/focus
        // logic when a section is selected, which collides with a 2.5-viewport pinned
        // section. Keep the still frame only.
        if (window.Shopify && Shopify.designMode) {
          this.goStatic();
          return;
        }

        this.init();
      }

      async init() {
        // Exit gate, complete BEFORE any frame preload.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.goStatic();
          return;
        }

        // Safari never implements the Network Information API, in any version. Its
        // absence means "no signal", never "bad signal" — only act when the interface
        // actually exists.
        if ('connection' in navigator && navigator.connection) {
          const connection = navigator.connection;
          const slowTypes = ['slow-2g', '2g', '3g'];
          if (connection.saveData || slowTypes.indexOf(connection.effectiveType) !== -1) {
            this.goStatic();
            return;
          }
        }

        if (typeof createImageBitmap !== 'function') {
          this.goStatic();
          return;
        }

        const avifSupported = await this.supportsAvif();
        if (!avifSupported) {
          this.goStatic();
          return;
        }

        // Format-Weiche: exactly one frame set is ever requested, never both.
        const desktopCount = parseInt(this.dataset.frameCountDesktop, 10) || 0;
        const mobileCount = parseInt(this.dataset.frameCountMobile, 10) || 0;
        const isDesktopViewport = window.matchMedia('(min-width: 750px)').matches;

        let template = '';
        let count = 0;
        let formatIsDesktop = true;
        if (isDesktopViewport && desktopCount > 0) {
          template = this.dataset.frameUrlTemplateDesktop;
          count = desktopCount;
          formatIsDesktop = true;
        } else if (!isDesktopViewport && mobileCount > 0) {
          template = this.dataset.frameUrlTemplateMobile;
          count = mobileCount;
          formatIsDesktop = false;
        } else if (desktopCount > 0) {
          template = this.dataset.frameUrlTemplateDesktop;
          count = desktopCount;
          formatIsDesktop = true;
        } else if (mobileCount > 0) {
          template = this.dataset.frameUrlTemplateMobile;
          count = mobileCount;
          formatIsDesktop = false;
        } else {
          this.goStatic();
          return;
        }

        this.template = template;
        this.totalFrames = count;
        this.stageCount = parseInt(this.dataset.stageCount, 10) || 0;

        // Plan 260815-lvl (S-a): the decoded-bitmap window radius. 0.12 * totalFrames lands
        // at +/-12 of 101 on desktop and +/-6 of 51 on mobile - the SAME physical scroll
        // distance (roughly 160px) on both formats, because the mobile sequence lays half
        // as many frames over the identical track height, so half the frame-radius covers
        // the same pixel span. Floored at 4 so a hypothetical very short sequence never
        // shrinks the window to something that stutters on its own.
        this.frameWindowRadius = Math.max(4, Math.round(this.totalFrames * FRAME_WINDOW_FRACTION));
        // Fix H5, lever 1: the eviction radius that actually gates memory pressure - wider
        // than the prefetch/priority window above by design, see the EVICTION_HYSTERESIS_FACTOR
        // comment at the top of the file.
        this.evictionRadius = this.frameWindowRadius * EVICTION_HYSTERESIS_FACTOR;

        // The canvas buffer is a fixed-resolution bitmap (its width/height content
        // attributes, not the CSS box size) that object-fit: cover crops against. It
        // must match the native resolution of whichever frame set was just chosen, or
        // a 9:16 mobile frame gets stretched into a 16:9 buffer (and vice versa) before
        // object-fit even runs - a visible distortion, not merely a wrong file. The
        // still <img> is already format-correct via <picture> art direction in the
        // markup; only this canvas buffer needs setting, and only here, before the
        // first drawForScroll() call below.
        if (formatIsDesktop) {
          this.elements.canvas.width = 1440;
          this.elements.canvas.height = 810;
        } else {
          this.elements.canvas.width = 810;
          this.elements.canvas.height = 1440;
        }
        // Plan 260815-lvl measurement hook: basis for peakResidentBitmapBytes in
        // tools/lab/hero-scrub-trace.mjs (peakFrameCacheSize * bytesPerFrame).
        this.bytesPerFrame = this.elements.canvas.width * this.elements.canvas.height * 4;

        // Frame 1 is already on the page as the still image. Adopt it into the frame
        // store immediately so the canvas never draws onto an empty surface. The
        // deferred script can run before the still image itself has finished
        // downloading (image loading is independent of DOM parsing), and
        // createImageBitmap() on an <img> that has not finished loading is
        // unreliable across browsers - wait for it explicitly instead of racing it.
        try {
          await this.waitForStillLoaded();
          const firstBitmap = await createImageBitmap(this.elements.still);
          this.frames.set(0, firstBitmap);
        } catch (error) {
          this.goStatic();
          return;
        }

        this.measure();
        window.addEventListener('resize', this.boundOnResize);
        window.addEventListener('orientationchange', this.boundOnResize);
        window.addEventListener('scroll', this.boundOnScroll, { passive: true });
        this.drawForScroll();

        // Plan 260815-lvl (S-a/S-b): ensureWindow() replaces preloadRemaining() as the
        // trigger that starts filling in frames beyond frame 0, still deferred past `load`
        // so it never competes with the LCP still image. Unlike the old unconditional
        // full-sequence preload, this call only decides what belongs in the window AROUND
        // the current (initial) position and queues exactly that - see ensureWindow().
        if (document.readyState === 'complete') {
          this.ensureWindow();
        } else {
          window.addEventListener('load', () => this.ensureWindow(), { once: true });
        }

        // Settling waits for the scroll to stop instead of following it continuously
        // (D-06 for the line switch; the same trigger now also drives the chapter snap, see
        // snapToNearestChapter()) - via the 150ms debounce in onScroll() below, which rides
        // the already-registered scroll listener (no second listener added). See the
        // boundOnSettle comment above for why this does not use 'scrollend'.
        // Direct call, not onSettle(): this sets the initial active line before any
        // scrolling has happened. Running snapToNearestChapter() here too would force an
        // unrequested scroll on page load, which is not a settle event.
        this.updateActiveLine();

        // Snap is armed only while the snap path lies ahead of the visitor: disarmed
        // once the sequence ends (onScroll below, at progress 1), once the track has
        // fully scrolled past (observer below), or on a deliberate jump past the
        // section (hint click below). Re-entering from below re-arms only after the
        // track has fully left the viewport above - see 02.2-RASTMECHANIK.md. This gate
        // now guards snapToNearestChapter() directly (this.snapArmed), not a CSS class -
        // native scroll-snap-type is retired, see hero-scroll.css (hero-scrub-ruckelt-live).
        this.snapObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) return;
            if (entry.boundingClientRect.top > 0) {
              this.armSnap();
            } else if (entry.boundingClientRect.bottom < 0) {
              this.disarmSnap();
            }
          });
        }, { threshold: 0 });
        this.snapObserver.observe(this.elements.track);

        if (this.elements.hint) {
          // The native anchor jump does the moving. Disarming first means a click never
          // races a pending chapter snap into fighting the jump; the default action (the
          // real anchor navigation) runs unmodified either way.
          this.elements.hint.addEventListener('click', () => {
            this.disarmSnap();
          });
        }

        // Opt-in, not opt-out: this is the only place the chapter snap is ever armed, and
        // only after every one of the exit checks above has already returned. All seven
        // goStatic() call sites return before this point, and with JavaScript disabled
        // nothing here ever runs - one code path covers all eight cases.
        this.armSnap();
      }

      goStatic() {
        this.classList.add('hero-scroll--static');
        if (this.elements.hint) {
          this.elements.hint.remove();
        }
      }

      waitForStillLoaded() {
        const img = this.elements.still;
        if (img.complete && img.naturalWidth > 0) {
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener(
            'error',
            () => reject(new Error('hero-scroll: still image failed to load')),
            { once: true }
          );
        });
      }

      supportsAvif() {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img.width === 1);
          img.onerror = () => resolve(false);
          img.src =
            'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
        });
      }

      // The section height (2.5 viewport heights, fixed via CSS) is measured once here
      // and on resize/orientationchange, never recomputed per drawn frame.
      measure() {
        this.viewportHeightPx = window.innerHeight;
        this.sectionHeightPx = this.elements.track.getBoundingClientRect().height;
      }

      frameUrlFor(index) {
        const padded = String(index + 1).padStart(3, '0');
        return this.template.replace(/-\d{3}\./, `-${padded}.`);
      }

      // Fix H5 Frame-Verfuegbarkeit (hero-scrub-ruckelt-live, 2026-08-16), lever 5: the set
      // of frame indices to actively PREFETCH around `center`, biased toward the direction
      // of travel - the real-trackpad trace showed a clear, consistent direction throughout
      // each fling, so frames ahead of it are more likely to be needed before frames already
      // passed. `direction` is -1/0/1 (see ensureWindow()). Frame 0 is pinned unconditionally,
      // not cosmetically: nearestLoadedFrame() falls back all the way to index 0 when nothing
      // nearer is loaded, and that fallback must always resolve to a real, already-decoded
      // bitmap (the adopted still image). This set drives PRIORITY only, never eviction any
      // more - see evictionKeepSet() below for the (much wider) set that actually protects a
      // frame from being closed.
      windowIndices(center, direction = 0) {
        const wanted = new Set([0]);
        const aheadBias = direction !== 0 ? Math.round(this.frameWindowRadius * 0.5) : 0;
        let loRadius = this.frameWindowRadius;
        let hiRadius = this.frameWindowRadius;
        if (direction > 0) {
          hiRadius += aheadBias;
          loRadius = Math.max(2, loRadius - aheadBias);
        } else if (direction < 0) {
          loRadius += aheadBias;
          hiRadius = Math.max(2, hiRadius - aheadBias);
        }
        const lo = Math.max(0, center - loRadius);
        const hi = Math.min(this.totalFrames - 1, center + hiRadius);
        for (let index = lo; index <= hi; index += 1) {
          wanted.add(index);
        }
        return wanted;
      }

      // Fix H5, lever 1: the (much wider) set of frame indices a bitmap is allowed to
      // survive in, built from this.evictionRadius rather than this.frameWindowRadius. This
      // is the generous hysteresis margin - a frame only ever gets closed once it falls
      // OUTSIDE this margin AND the resident count is over the cap (see evictOverCap()), not
      // simply because it fell outside the narrow prefetch window. It is also what a
      // just-landed fetch is checked against (see fetchFrame()), so a fetch that was still
      // in flight while the center moved a little is not thrown away the instant it lands.
      evictionKeepSet(center) {
        const keep = new Set([0]);
        const lo = Math.max(0, center - this.evictionRadius);
        const hi = Math.min(this.totalFrames - 1, center + this.evictionRadius);
        for (let index = lo; index <= hi; index += 1) {
          keep.add(index);
        }
        return keep;
      }

      // Fix H5, lever 1 + 4: evicts ONLY when actually over the memory cap
      // (2*evictionRadius + 2), never as a side effect of the center moving - the confirmed
      // root cause was ensureWindow() evicting synchronously on every recompute regardless
      // of whether memory pressure existed. Evicts the frames FARTHEST from `center` first
      // (never frame 0), so resident cannot collapse to a single frame mid-gesture the way
      // it was measured to (residentMin 1, ECHTMESSUNG 2026-08-16) - the floor is structural
      // here, not a bolted-on minimum, because eviction only ever removes the SURPLUS above
      // the cap, never chases the window down to nothing.
      evictOverCap(center) {
        const cap = this.evictionRadius * 2 + 2;
        if (this.frames.size <= cap) return;
        const candidates = Array.from(this.frames.keys())
          .filter((index) => index !== 0)
          .sort((a, b) => Math.abs(b - center) - Math.abs(a - center));
        while (this.frames.size > cap && candidates.length > 0) {
          const index = candidates.shift();
          const bitmap = this.frames.get(index);
          if (bitmap && typeof bitmap.close === 'function') bitmap.close();
          this.frames.delete(index);
          this.closedBitmapCount += 1;
        }
      }

      // Fix H5, lever 2: fetches the SINGLE frame currently on screen immediately, bypassing
      // every queue and every brake (idle callback, concurrency limit, direction bias) - a
      // visitor tolerates a sparse cache, but never a visibly wrong frame. This is a no-op
      // fire-and-forget call; fetchFrame() itself de-duplicates against this.inFlight, so it
      // is safe to call on every single ensureWindow() (i.e. up to once per scroll rAF).
      ensureTargetLoaded(index) {
        if (this.frames.has(index) || this.failedFrames.has(index) || this.inFlight.has(index)) return;
        this.fetchFrame(index);
      }

      // Fix H5: recomputes the current window around the frame the visitor is actually at.
      // Unlike the prior (M005) version, this NEVER evicts as a direct consequence of the
      // center moving - see evictOverCap() above, which only trims real surplus over the
      // memory cap. Called on load/scroll (see init()/onScroll()) and once more on settle
      // (see onSettle()) so the idle-gated background top-up also gets a fresh pass once a
      // gesture has actually ended, not just while it is still in flight.
      ensureWindow() {
        if (this.totalFrames === 0) return;
        const center = Math.min(this.totalFrames - 1, Math.floor(this.progress() * this.totalFrames));
        const direction = Math.sign(center - this.lastCenter);
        this.lastCenter = center;

        this.ensureTargetLoaded(center);
        this.evictOverCap(center);

        // A stale queue from a since-moved center is worthless - discarded, not appended to.
        const wanted = this.windowIndices(center, direction);
        this.loaderQueue = Array.from(wanted)
          .filter((index) => !this.frames.has(index) && !this.failedFrames.has(index) && !this.inFlight.has(index))
          .sort((a, b) => Math.abs(a - center) - Math.abs(b - center));

        this.runLoader();
      }

      // Fix H5: the actual network+decode fetch for one frame index, shared by the immediate
      // target path (ensureTargetLoaded()) and the background loader (runLoader()) so there
      // is exactly one place that can adopt or discard a landed bitmap. Tracks in-flight
      // indices in this.inFlight so the two paths can never race each other into fetching the
      // same frame twice.
      async fetchFrame(index) {
        if (this.frames.has(index) || this.failedFrames.has(index) || this.inFlight.has(index)) return;
        this.inFlight.add(index);
        try {
          // Shopify's CDN content-negotiates on the Accept header (Vary: Accept) and
          // falls back to a JPEG transcode roughly 6x heavier than the source AVIF
          // when the request does not explicitly advertise AVIF support. The <img>
          // tag for frame 1 sends that automatically via the browser's own image
          // Accept list; a bare fetch() defaults to Accept: */* and silently blows
          // the byte budget. A short "image/avif" alone is NOT enough either -
          // empirically confirmed the CDN's content negotiation only recognizes the
          // full realistic browser image-Accept list, so that exact list is sent
          // here, matching what Chrome sends for an <img> element.
          const response = await fetch(this.frameUrlFor(index), {
            headers: {
              Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
            },
          });
          const blob = await response.blob();
          const bitmap = await createImageBitmap(blob);

          // The center can have moved on while this fetch/decode was in flight. Only adopt
          // the bitmap if the index is still within the (generous, hysteresis-widened)
          // eviction margin - not the narrow prefetch window, which would throw away a
          // just-landed fetch far too eagerly, one of the two starvation mechanisms
          // ECHTMESSUNG 2026-08-16 found directly in the trace.
          const center = Math.min(this.totalFrames - 1, Math.floor(this.progress() * this.totalFrames));
          if (this.evictionKeepSet(center).has(index)) {
            this.frames.set(index, bitmap);
            this.requestRedraw();
          } else {
            if (typeof bitmap.close === 'function') bitmap.close();
            this.closedBitmapCount += 1;
          }
        } catch (error) {
          // A single failed frame is remembered so a permanently broken asset is not
          // re-requested on every scroll; nearestLoadedFrame() still finds the closest
          // bitmap that did load, and the sequence keeps going.
          this.failedFrames.add(index);
        } finally {
          this.inFlight.delete(index);
        }
      }

      // Fix H5, lever 3: the background top-up loader (replaces preloadRemaining()/
      // stagePreloadOrder(), same as the M005 version this replaces). Runs the queue
      // ensureWindow() just built, MAX_CONCURRENT_LOADS frames at a time instead of one at a
      // time, guarded by this.loaderRunning so ensureWindow() can call it repeatedly without
      // spawning parallel loader loops. The pacing between batches is the actual lever-3 fix:
      // requestIdleCallback is used ONLY at rest (this.gestureActive === false); during an
      // active gesture it paces on requestAnimationFrame instead, because idle callbacks can
      // legitimately wait their full 100ms timeout when the main thread is busy driving the
      // scroll - exactly when the loader can least afford to wait, and exactly the mechanism
      // ECHTMESSUNG 2026-08-16 found starving the cache to residentMin 1 during a fling.
      async runLoader() {
        if (this.loaderRunning) return;
        this.loaderRunning = true;
        try {
          while (this.loaderQueue.length > 0) {
            await (this.gestureActive ? this.nextFrame() : this.idle());
            const batch = [];
            while (batch.length < MAX_CONCURRENT_LOADS && this.loaderQueue.length > 0) {
              const index = this.loaderQueue.shift();
              if (index === undefined) continue;
              if (this.frames.has(index) || this.failedFrames.has(index) || this.inFlight.has(index)) continue;
              batch.push(index);
            }
            if (batch.length === 0) continue;
            await Promise.all(batch.map((index) => this.fetchFrame(index)));
          }
        } finally {
          this.loaderRunning = false;
        }
      }

      // Fix H5, lever 3: the active-gesture pacing primitive - one requestAnimationFrame,
      // promisified. Cheap and frequent, unlike idle(), by design: an active gesture means
      // the main thread is never truly idle in the first place (see runLoader() comment).
      nextFrame() {
        return new Promise((resolve) => requestAnimationFrame(() => resolve()));
      }

      // Safari implements neither requestIdleCallback nor a scheduler equivalent; the
      // setTimeout fallback is not decoration, it is the only way frames load there at all.
      // Used only for the AT-REST background top-up now (see runLoader()) - the 100ms
      // timeout is an acceptable brake once nothing is competing with it, which is exactly
      // the case it was designed for; it stopped being acceptable only while a gesture was
      // still active, which is why it no longer gates that path.
      idle() {
        return new Promise((resolve) => {
          if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(() => resolve(), { timeout: 100 });
          } else {
            setTimeout(resolve, 16);
          }
        });
      }

      onScroll() {
        // Fix H5, lever 3: set unconditionally, even on the early-return path below, so the
        // loader's pacing (see runLoader()) always sees an up-to-date "are we mid-gesture"
        // signal - never gated behind the same rAF-coalescing this.ticking guards.
        this.gestureActive = true;
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.drawForScroll();
          this.ensureWindow();
          if (this.lastProgress >= 1) {
            this.disarmSnap();
          }
          this.ticking = false;
        });
        if (!this.hintGone && this.elements.hint) {
          this.hintGone = true;
          this.elements.hint.classList.add('hero-scroll__hint--settled');
        }
        // Every scroll event pushes the settle point 150ms further out, so this only
        // actually fires once no further scroll event has arrived for 150ms straight -
        // unconditionally, not just as a fallback (see boundOnSettle comment above).
        clearTimeout(this.settleDebounceTimer);
        this.settleDebounceTimer = setTimeout(this.boundOnSettle, 150);
      }

      progress() {
        const scrollableRange = this.sectionHeightPx - this.viewportHeightPx;
        if (scrollableRange <= 0) return 0;
        const boundingTop = this.elements.track.getBoundingClientRect().top;
        return Math.min(1, Math.max(0, -boundingTop / scrollableRange));
      }

      nearestLoadedFrame(targetIndex) {
        if (this.frames.has(targetIndex)) return targetIndex;
        for (let distance = 1; distance < this.totalFrames; distance += 1) {
          if (this.frames.has(targetIndex - distance)) return targetIndex - distance;
          if (this.frames.has(targetIndex + distance)) return targetIndex + distance;
        }
        return 0;
      }

      drawForScroll() {
        if (!this.ctx || this.totalFrames === 0) return;

        const progress = this.progress();
        this.lastProgress = progress;

        // Frame index: the linear share of progress in the frame count, no easing,
        // no acceleration, no snapping.
        const targetIndex = Math.min(this.totalFrames - 1, Math.floor(progress * this.totalFrames));
        const drawIndex = this.nearestLoadedFrame(targetIndex);
        // Plan 260815-lvl measurement hooks, read directly by the lab tools.
        this.lastTargetIndex = targetIndex;
        this.lastDrawnIndex = drawIndex;
        const bitmap = this.frames.get(drawIndex);
        if (bitmap) {
          this.ctx.clearRect(0, 0, this.elements.canvas.width, this.elements.canvas.height);
          this.ctx.drawImage(bitmap, 0, 0, this.elements.canvas.width, this.elements.canvas.height);
          if (!this.canvasReady) {
            this.canvasReady = true;
            this.elements.canvas.classList.add('hero-scroll__canvas--ready');
          }
        }
      }

      // Plan 260815-lvl (S-b): coalesces every drawForScroll() call the loader would
      // otherwise trigger synchronously (once per decoded frame, up to ~100 times during
      // the first scroll on M004) into a single requestAnimationFrame per already-pending
      // request. This is the only place drawForScroll() is invoked from the load path; the
      // other two call sites are the initial draw in init() and the scroll-driven rAF
      // callback in onScroll() - three call sites total, none of them outside a rAF.
      requestRedraw() {
        if (this.redrawRequested) return;
        this.redrawRequested = true;
        requestAnimationFrame(() => {
          this.redrawRequested = false;
          this.drawForScroll();
        });
      }

      // Runs only once the scroll has settled (150ms debounce, see boundOnSettle), never on
      // every scroll event (D-06) - the active line waits for the movement to settle.
      updateActiveLine() {
        if (this.stageCount === 0) return;
        const progress = this.progress();
        // A visitor who lands exactly on a stage boundary (either via snapToNearestChapter()
        // below, or by scrolling there unassisted) lands at a scrollY the browser has already
        // rounded to a whole device pixel. Because the boundaries sit at fractional-pixel
        // offsets (svh-based percentages of the track height), that rounding can leave
        // progress() a hair under the exact stage boundary - measured live at boundary 3
        // (0.399931 instead of 0.4) - and Math.floor() would then read the previous stage at
        // the instant a visitor lands on the new one. STAGE_EPSILON absorbs a few pixels of
        // that unavoidable rounding; it is far smaller than the 1/stageCount gap between real
        // stage boundaries, so it cannot trigger an early transition mid-scroll.
        const STAGE_EPSILON = 0.004;
        const stage = Math.min(this.stageCount - 1, Math.floor(progress * this.stageCount + STAGE_EPSILON));
        if (stage === this.currentStage) return;
        this.currentStage = stage;
        this.elements.lines.forEach((line) => {
          const lineStage = parseInt(line.dataset.stage, 10);
          line.classList.toggle('hero-scroll__line--active', lineStage === stage);
        });
      }

      // Fires on the same settle trigger as the active-line switch (the 150ms debounce, see
      // boundOnSettle above). Order matters only in that both read progress() independently
      // and neither depends on the other's result.
      onSettle() {
        // Fix H5, lever 3: the gesture is over - the loader's pacing switches from
        // requestAnimationFrame back to the idle-gated brake from here on (see runLoader()).
        this.gestureActive = false;
        this.updateActiveLine();
        this.snapToNearestChapter();
        // Fix H5, lever 3/4: one more window recompute now that the gesture has genuinely
        // ended, so the background loader gets a full, fresh queue to top up the window at
        // rest. This is the fix for the SECOND, quieter half of the confirmed starvation bug
        // (ECHTMESSUNG 2026-08-16: resident stalled at 13 of 26 possible frames for 27
        // seconds of idle time) - previously nothing ever called ensureWindow() again once
        // scrolling stopped, so the still-narrow M005 window simply never finished filling.
        this.ensureWindow();
      }

      // Replaces the retired native CSS scroll-snap-type mechanism (see hero-scroll.css,
      // debug session hero-scrub-ruckelt-live): that mechanism let the browser compositor
      // re-correct scroll position after every discrete wheel/touch tick, because a browser
      // cannot reliably tell a mid-gesture tick from a gesture end - measured as a 19x
      // increase in frame-to-frame scroll variance versus no snap at all, identical input.
      // Running the snap only here, after the gesture has already fully settled, removes
      // that interference: nothing ever corrects the scroll position while a visitor is
      // still actively scrolling, only once they have already stopped.
      snapToNearestChapter() {
        if (!this.snapArmed) return;
        if (this.stageCount <= 0) return;
        // An explicit, permanent opt-out: no automatic movement is ever triggered for a
        // visitor who has asked for reduced motion, matching the exit gate at the top of
        // init() that keeps such visitors on the static path in the first place.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const scrollableRange = this.sectionHeightPx - this.viewportHeightPx;
        if (scrollableRange <= 0) return;

        const progress = this.progress();
        const nearestStep = Math.round(progress * this.stageCount);
        const targetProgress = Math.min(1, Math.max(0, nearestStep / this.stageCount));

        // The track's document-coordinate top does not move as the page scrolls (only its
        // viewport-relative rect does), so it is recomputed fresh from the current rect on
        // every call rather than cached - measure() only runs on load/resize, not per settle.
        const trackRect = this.elements.track.getBoundingClientRect();
        const trackDocTop = trackRect.top + window.scrollY;
        const targetScrollY = trackDocTop + targetProgress * scrollableRange;

        // Already there (within a device pixel): skip the call entirely rather than issue a
        // no-op smooth-scroll animation to the position the visitor is already resting at.
        if (Math.abs(targetScrollY - window.scrollY) < 1) return;
        window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
      }

      armSnap() {
        this.snapArmed = true;
      }

      disarmSnap() {
        this.snapArmed = false;
      }
    }
  );
}
